import { createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    AuctionCalendarPayload,
    TodaysCatalogCount,
    UPCOMING_AUCTIONS_COUNT_CACHE_TIME,
} from '@/redux/modules/auctionCalendar/auctionCalendar.types';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { getDeployment } from '@/redux/modules/config';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { GlobalState } from '@/redux/store';

import { getCalendarResultsApi, getCatalogsTodayCountApi, SearchAuctionFacet } from './auctionCalendar.api';
import {
    getAuctionCalendarFacets,
    getAuctionCalendarIds,
    getCatalogsTodayCount,
    getUpcomingAuctionsCountLoadTime,
} from './auctionCalendar.selectors';

const getDefaultCalendarQuery = (state: GlobalState) => ({
    client: 'web',
    client_version: '5.0.0',
    excludedHouses: JSON.stringify(getUserExcludedHouses(state)),
    max_facet_values: 3,
    offset: new Date().getTimezoneOffset(),
});

export const fetchAuctionCalendarResults = createAsyncThunk<AuctionCalendarPayload, any>(
    'la/ui/auctionCalendar/fetchAuctionCalendarResults',
    async (queryParams, { getState }) => {
        const state = getState();
        const response = await getCalendarResultsApi({
            deployment: getDeployment(state),
            queryParams: {
                ...getDefaultCalendarQuery(state),
                pagesize: queryParams.pageSize || queryParams.pagesize,
                ...queryParams,
            },
        });
        return response.payload;
    }
);

// Same query as the Auction Calendar, so the homepage count matches /catalog/search/ (LAAC-2589)
export const fetchUpcomingAuctionsCount = createAsyncThunk<number, void>(
    'la/ui/auctionCalendar/fetchUpcomingAuctionsCount',
    async (_, { getState }) => {
        const state = getState();
        const response = await getCalendarResultsApi({
            deployment: getDeployment(state),
            queryParams: { ...getDefaultCalendarQuery(state), pagesize: 1 },
        });
        return response.payload.count;
    },
    {
        condition: (_, { getState }) =>
            Date.now() - getUpcomingAuctionsCountLoadTime(getState()) >= UPCOMING_AUCTIONS_COUNT_CACHE_TIME,
    }
);

export const fetchCatalogsTodayCount = createAsyncThunk<TodaysCatalogCount, void>(
    'la/ui/auctionCalendar/fetchCatalogsTodayCount',
    async (_, { getState }) => {
        const deployment = getDeployment(getState());
        const response = await getCatalogsTodayCountApi({ deployment });
        return response.data[0];
    }
);

export const fetchCatalogsTodayCountIfNeeded = createAsyncThunk<void, void>(
    'la/ui/auctionCalendar/fetchCatalogsTodayCountIfNeeded',
    async (_, { dispatch, getState }) => {
        const todayCount = getCatalogsTodayCount(getState());
        if (!todayCount) {
            await dispatch(fetchCatalogsTodayCount());
        }
    }
);

export const updateFacetsSelected = createAsyncThunk<{ facets: SearchAuctionFacet[] }, void>(
    'la/ui/auctionCalendar/updateFacetsSelected',
    (_, { getState }) => ({
        facets: getAuctionCalendarFacets(getState()),
    })
);

export const fetchDataForAuctionCalendarPage = createAsyncThunk<void, any>(
    'la/ui/auctionCalendar/fetchDataForAuctionCalendarPage',
    async (queryParams, { dispatch, getState }) => {
        await Promise.all([
            dispatch(fetchCatalogsTodayCountIfNeeded()),
            dispatch(fetchCatalogAdIfNeeded({ pageName: 'calendar' })),
        ]);
        // in order to re-arrange the facets set in state we need to adjust them after the search call
        await dispatch(fetchAuctionCalendarResults(queryParams));
        await dispatch(updateFacetsSelected());

        const catalogIds = getAuctionCalendarIds(getState());
        await dispatch(fetchCatalogsIfNeeded({ catalogIds }));
    }
);
